// Community creations (ADR 0004): schemas of each type's content, of the manifest and of the related routes.
// Validated identically by the browser (local drafts, previews), the API (publishing) and the game server (rooms).
// The bounds are wide but finite: fine consistency (gradients, crossings, load) is checked by each type.
import { z } from 'zod/v4';

export const CREATION_KINDS = ['workout', 'plan', 'track', 'mode', 'item', 'theme', 'cosmetic', 'game'] as const;
export const CreationKind = z.enum(CREATION_KINDS);
export type CreationKind = z.infer<typeof CreationKind>;

export const CreationId = z.string().regex(/^[a-z0-9][a-z0-9_-]{1,59}$/i, 'Invalid identifier');
const text = (max: number) => z.string().trim().max(max);
const name = (max: number) => z.string().trim().min(1).max(max);
const frac = z.number().min(0).max(1);
const Hex = z.string().regex(/^#[0-9a-f]{6}$/i, 'Colour #rrggbb expected');

// ---------- Workout ----------
const Cadence = z.number().int().min(40).max(140);
const Interval = z.object({ dur: z.number().min(1).max(7200), p: z.number().min(0).max(4), cad: Cadence.optional() });
export const Segment = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('steady'), dur: z.number().min(1).max(14400), p: z.number().min(0).max(4).optional(), w: z.number().min(0).max(2500).optional(), cad: Cadence.optional(), text: text(80).optional() }),
  z.object({ kind: z.literal('ramp'), dur: z.number().min(1).max(14400), p0: z.number().min(0).max(4), p1: z.number().min(0).max(4), cad: Cadence.optional(), text: text(80).optional() }),
  z.object({ kind: z.literal('intervals'), reps: z.number().int().min(1).max(100), on: Interval, off: Interval, text: text(80).optional() }),
  z.object({ kind: z.literal('free'), dur: z.number().min(1).max(14400), text: text(80).optional() }),
]);
export const WORKOUT_CATEGORIES = ['endurance', 'tempo', 'seuil', 'vo2', 'anaerobie', 'recup', 'test'] as const;
export const WorkoutContent = z.object({
  cat: z.string().max(20),
  test: z.enum(['ftp20', 'ramp']).optional(),
  segs: z.array(Segment).min(1).max(300),
});
export type WorkoutContent = z.infer<typeof WorkoutContent>;

// ---------- Plan ----------
export const PlanSession = z.object({
  /** Id of an embedded workout (`workouts`) or of the library. */
  workout: z.string().max(60),
  /** Suggested day, 1 = Monday. */
  day: z.number().int().min(1).max(7).optional(),
  note: text(120).optional(),
  /** Optional workout: skipped by the "missed" rule when falling behind. */
  optional: z.boolean().optional(),
});
/** Adaptation rules of a plan, evaluated after each workout (packages/creations/src/adapt.ts). */
export const AdaptRule = z.discriminatedUnion('when', [
  z.object({ when: z.literal('fatigue'), tsbBelow: z.number().min(-60).max(0).default(-20), workout: z.string().max(60).default('recup30') }),
  z.object({ when: z.literal('failed'), complianceBelow: z.number().min(30).max(95).default(70), easier: z.number().min(0.02).max(0.15).default(0.05) }),
  z.object({ when: z.literal('crushed'), complianceAbove: z.number().min(85).max(100).default(97), harder: z.number().min(0.02).max(0.1).default(0.03) }),
  z.object({ when: z.literal('missed'), days: z.number().int().min(2).max(21).default(5), then: z.enum(['drop-optional', 'notice']).default('drop-optional') }),
]);
export type AdaptRule = z.infer<typeof AdaptRule>;
/** Current adjustments of a plan (player state, synchronised with their account). */
export const PlanAdjust = z.object({
  intensity: z.number().min(0.7).max(1.2).default(1),
  swaps: z.record(z.string().max(12), z.string().max(60)).default({}),
  evaluated: z.number().default(0),
  log: z.array(z.object({ at: z.number(), text: text(200) })).max(30).default([]),
});
export type PlanAdjust = z.infer<typeof PlanAdjust>;
export const EmbeddedWorkout = WorkoutContent.extend({ id: z.string().regex(/^[a-z0-9_-]{1,60}$/i), name: name(80), desc: text(400).optional() });
export const PlanContent = z.object({
  goal: text(200),
  level: z.enum(['debutant', 'intermediaire', 'avance']).optional(),
  weeks: z.array(z.object({ focus: text(80).optional(), sessions: z.array(PlanSession).min(1).max(7) })).min(1).max(24),
  /** Workouts belonging to the plan: it stays playable offline and shareable as is. */
  workouts: z.array(EmbeddedWorkout).max(60).default([]),
  adapt: z.array(AdaptRule).max(8).default([]),
});
export type PlanContent = z.infer<typeof PlanContent>;

// ---------- Circuit ----------
export const CourseSegment = z.object({
  len: z.number().min(20).max(5000).describe('segment length in metres'),
  turn: z.number().min(-720).max(720).describe('cumulative turn over the segment in degrees, positive to the right'),
  grade: z.number().min(-30).max(30).describe('average gradient in %'),
  label: text(40).optional(),
});
// ---------- World (what surrounds a track) ----------
// Render-only data, described along the route: the engine compiles and bounds it (ADR 0020). The race never reads it.
export const ROAD_SURFACES = ['asphalt', 'smooth', 'worn', 'concrete', 'cobbles', 'brick', 'gravel', 'dirt', 'sand', 'snow', 'boards', 'neon'] as const;
export const ROAD_EDGES = ['verge', 'kerb', 'race', 'pavement', 'guardrail', 'railing', 'wall', 'hedge', 'fence', 'none'] as const;
export const ROAD_STRUCTURES = ['ground', 'bridge', 'tunnel', 'cutting'] as const;
export const BRIDGE_STYLES = ['stone', 'steel', 'suspension', 'wood'] as const;
export const DISTRICT_PRESETS = [
  'countryside',
  'vineyard',
  'forest',
  'alpine',
  'meadow',
  'desert',
  'beach',
  'park',
  'village',
  'old-town',
  'suburb',
  'downtown',
  'skyscrapers',
  'industrial',
  'port',
  'wasteland',
  'empty',
] as const;
export const WINDOW_STYLES = ['grid', 'shutters', 'ribbon', 'glass', 'arched', 'none'] as const;
export const ROOF_STYLES = ['flat', 'gable', 'steep', 'hip'] as const;
export const WATER_KINDS = ['lake', 'river', 'sea'] as const;
export const HORIZONS = ['mountains', 'hills', 'flat', 'skyline'] as const;
export const LANDMARK_PRESETS = [
  'windmill',
  'church',
  'castle',
  'lighthouse',
  'stadium',
  'tower',
  'obelisk',
  'ferris-wheel',
  'wind-turbine',
  'gantry',
  'torii',
  'pyramid',
  'water-tower',
  'palm-group',
  'rock-arch',
] as const;
export const PART_SHAPES = ['box', 'cylinder', 'cone', 'sphere', 'dome', 'pyramid', 'torus', 'arch'] as const;
const Side = z.enum(['left', 'right']);
const DistrictId = z.string().regex(/^[a-z0-9-]{1,24}$/);

/** A custom district: a preset, adjusted. Everything left out keeps the preset's value. */
export const WorldDistrict = z.object({
  id: DistrictId,
  preset: z.enum(DISTRICT_PRESETS),
  floors: z.tuple([z.number().int().min(1).max(60), z.number().int().min(1).max(60)]).optional(),
  density: frac.optional(),
  setback: z.number().min(1).max(60).optional().describe('metres from the road edge to the first building'),
  facades: z.array(Hex).min(1).max(8).optional(),
  roofs: z.array(Hex).min(1).max(6).optional(),
  windows: z.enum(WINDOW_STYLES).optional(),
  roof: z.enum(ROOF_STYLES).optional(),
  shops: z.boolean().optional(),
  ground: Hex.optional().describe('colour of the ground'),
  trees: z.number().min(0).max(2).optional(),
  relief: z.number().min(0).max(2).optional().describe('height of the hills: 0 flat, 1 usual, 2 steep'),
});
export const WorldStretch = z.object({
  from: frac,
  to: frac,
  surface: z.enum(ROAD_SURFACES).optional(),
  color: Hex.optional().describe('tint of the road surface'),
  markings: z.boolean().optional(),
  edge: z.enum(ROAD_EDGES).optional(),
  structure: z.enum(ROAD_STRUCTURES).optional(),
  bridge: z.enum(BRIDGE_STYLES).optional(),
  district: DistrictId.optional(),
  crowd: z.number().min(0).max(2).optional(),
});
export const WorldWater = z.object({
  kind: z.enum(WATER_KINDS),
  at: frac.describe('where along the lap (0 to 1) it sits or crosses'),
  side: Side.optional(),
  distance: z.number().min(0).max(1500).optional().describe('metres from the road'),
  size: z.number().min(10).max(1500).optional().describe('lake radius or river width, metres'),
  color: Hex.optional(),
});
const PartDim = z.number().min(0.05).max(400);
export const LandmarkPart = z.object({
  shape: z.enum(PART_SHAPES),
  x: z.number().min(-200).max(200).optional(),
  y: z.number().min(-20).max(500).optional(),
  z: z.number().min(-200).max(200).optional(),
  w: PartDim,
  h: PartDim,
  d: PartDim.optional(),
  yaw: z.number().min(-180).max(180).optional(),
  tilt: z.number().min(-180).max(180).optional(),
  color: Hex,
  finish: z.enum(['matte', 'metal', 'glow']).optional(),
  spin: z.boolean().optional().describe('turns around its own centre, like a wheel or a rotor'),
});
export const WorldLandmark = z
  .object({
    preset: z.enum(LANDMARK_PRESETS).optional(),
    parts: z.array(LandmarkPart).min(1).max(40).optional(),
    at: frac,
    side: Side.optional(),
    distance: z.number().min(0).max(1500).optional().describe('metres from the road; the engine keeps it off the road'),
    over: z.boolean().optional().describe('stands across the road, like a gate: the riders pass under it'),
    scale: z.number().min(0.2).max(5).optional(),
    color: Hex.optional().describe('main colour of a preset'),
    every: z.number().min(25).max(5000).optional().describe('repeats along the road every so many metres'),
    until: frac.optional().describe('where the repetition stops'),
  })
  .refine(l => !!l.preset !== !!l.parts, 'A landmark has either a preset or parts');
export const WorldContent = z.object({
  /** District of everything no stretch names. */
  base: DistrictId.optional(),
  horizon: z.enum(HORIZONS).optional(),
  stretches: z.array(WorldStretch).max(24).optional(),
  districts: z.array(WorldDistrict).max(8).optional(),
  water: z.array(WorldWater).max(6).optional(),
  landmarks: z.array(WorldLandmark).max(16).optional(),
});
export type WorldContent = z.infer<typeof WorldContent>;
export type WorldDistrict = z.infer<typeof WorldDistrict>;
export type WorldStretch = z.infer<typeof WorldStretch>;
export type WorldWater = z.infer<typeof WorldWater>;
export type WorldLandmark = z.infer<typeof WorldLandmark>;
export type LandmarkPart = z.infer<typeof LandmarkPart>;

export const TRACK_THEMES = ['campagne', 'montagne', 'ville', 'cote', 'foret'] as const;
export const TrackContent = z.object({
  theme: z.enum(TRACK_THEMES).optional(),
  laps: z.number().int().min(1).max(10).optional(),
  /** Layout by segments (closed automatically), or control points [x, z, altitude] for drawn tracks. */
  segments: z.array(CourseSegment).min(2).max(80).optional(),
  points: z.array(z.tuple([z.number().finite(), z.number().finite(), z.number().finite()])).min(4).max(400).optional(),
  /** A road from A to B (ADR 0029): start on the first point or segment, finish at the end, ridden once; never closed. */
  open: z.boolean().optional().describe('true for a road from A to B (start and finish in different places), absent for a loop'),
  /** Real altitude of the lowest point (m): thinner air at altitude slows you less. */
  altitude: z.number().min(-500).max(6000).optional().describe('real altitude of the lowest point in metres'),
  /** What surrounds the road (render only). Absent: built from `theme`. */
  world: WorldContent.optional(),
  /** The look this track is meant to be ridden in; a player can keep their own mood (setting `trackMood`). */
  mood: z.lazy(() => ThemeContent).optional(),
}).refine(t => !!t.segments !== !!t.points, 'A track has either segments or points');
export type TrackContent = z.infer<typeof TrackContent>;
/** What the client reads of a track it met in a room, to dress it. */
export const TrackLook = z.object({ world: WorldContent.optional(), mood: z.lazy(() => ThemeContent).optional(), theme: z.enum(TRACK_THEMES).optional() });
/** Track ready for the engine (points), as embedded in an online room. */
export const TrackDefSchema = z.object({
  id: z.string().max(80).optional(), name: name(60), desc: text(200).optional(),
  pts: z.array(z.tuple([z.number().min(-1e5).max(1e5), z.number().min(-1e5).max(1e5), z.number().min(-1000).max(9000)])).min(4).max(400),
  alt0: z.number().min(-500).max(6000).optional(),
  open: z.boolean().optional(),
});

// ---------- Mode ----------
/** List duplicated from core/items.ts (contracts depends only on zod); a test checks that they stay equal. */
export const ITEM_IDS = ['turbo', 'banana', 'shell', 'shield', 'magnet', 'jam', 'storm', 'fog', 'bomb', 'crash'] as const;
export const ItemIdSchema = z.enum(ITEM_IDS);
const boost = z.number().min(0).max(5);

// ---------- Objet ----------
export const ITEM_TARGETS = ['self', 'ahead', 'leader', 'allAhead', 'all', 'behind'] as const;
export const ItemContent = z.object({
  icon: z.string().min(1).max(8),
  /** Who is hit: oneself, the rider ahead, the leader, everyone ahead, everyone, or whoever rides over the trap. */
  target: z.enum(ITEM_TARGETS),
  /** Immediate effect, trap dropped behind oneself, or missile chasing its target. */
  delivery: z.enum(['instant', 'trap', 'missile']),
  duration: z.number().min(0.5).max(15),
  effect: z.object({
    power: z.number().min(0.3).max(2.5).optional().describe('power multiplier'),
    drag: z.number().min(0.4).max(3).optional().describe('aerodynamic drag multiplier'),
    speed: z.number().min(0.2).max(1).optional().describe('speed kept on impact'),
    shield: z.number().min(1).max(15).optional().describe('shield (s), on oneself'),
    draft: z.number().min(0).max(0.4).optional().describe('forced draft'),
    jam: z.boolean().optional().describe('gears locked'),
    spin: z.boolean().optional().describe('dazed rider (visual)'),
    grade: z.number().min(-8).max(8).optional().describe('added felt gradient (%) on the trainer'),
    wind: z.number().min(-8).max(15).optional().describe('felt wind (m/s) on the trainer'),
  }),
  blockable: z.boolean().default(true),
  /** Draw weights at the front and at the back of the race. */
  odds: z.object({ front: z.number().min(0).max(40), back: z.number().min(0).max(40) }),
});
export type ItemContent = z.infer<typeof ItemContent>;
/** Item embedded in the rules of a race (playable offline and in a room). */
export const CustomItem = z.object({ id: z.string().regex(/^[a-z0-9_-]{2,60}$/i), name: name(40), help: text(160).optional(), content: ItemContent });
export type CustomItem = z.infer<typeof CustomItem>;
export const ModeRulesSchema = z.object({
  finish: z.enum(['laps', 'survivor', 'timer', 'open']),
  laps: z.number().int().min(1).max(20).optional(),
  timeLimit: z.number().int().min(0).max(3 * 3600).optional(),
  items: z.boolean(),
  itemPool: z.array(ItemIdSchema).max(ITEM_IDS.length).optional(),
  itemBoost: z.object(Object.fromEntries(ITEM_IDS.map(k => [k, boost.optional()])) as Record<(typeof ITEM_IDS)[number], z.ZodOptional<typeof boost>>).optional(),
  arena: z.boolean().optional(),
  eliminateLast: z.boolean().optional(),
  bots: z.boolean(),
  pace: z.enum(['race', 'group']).optional(),
  draft: z.boolean().optional(),
  rubberBand: z.boolean().optional(),
  ghost: z.boolean().optional(),
  online: z.boolean(),
  scoring: z.enum(['position', 'hits']).optional(),
  /** Created items, in addition to (or instead of, with builtinItems: false) the official items. */
  customItems: z.array(CustomItem).max(12).optional(),
  builtinItems: z.boolean().optional(),
  /** Team play (core TeamRules): by club or balanced, team size, how a team is scored, start together or in waves, drafting behind teammates only. */
  teams: z.object({
    by: z.enum(['club', 'auto']).default('auto'),
    size: z.number().int().min(2).max(8).default(4),
    scoring: z.enum(['nth', 'best', 'ranks']).default('nth'),
    nth: z.number().int().min(1).max(8).optional(),
    start: z.enum(['together', 'waves']).default('together'),
    waveGap: z.number().int().min(10).max(600).optional(),
    draftTeamOnly: z.boolean().optional(),
  }).optional(),
});
export const ModeContent = z.object({ icon: z.string().max(8).default('🏁'), rules: ModeRulesSchema });
export type ModeContent = z.infer<typeof ModeContent>;

// ---------- Theme (visual mood) ----------
export const WEATHERS = ['clair', 'pluie', 'neige', 'brume', 'orage'] as const;
export const ThemeContent = z.object({
  sky: z.object({
    sunElevation: z.number().min(-4).max(85), sunAzimuth: z.number().min(0).max(360), haze: frac,
    sun: Hex, sky: Hex, fog: Hex, cloud: Hex,
    fogNear: z.number().min(40).max(2000), fogFar: z.number().min(300).max(6000),
    /** How much the visible sky takes `sky` (top) and `fog` (horizon) instead of Earth's atmosphere: 0 or absent, Earth; 1, entirely. */
    tint: frac.optional(),
    exposure: z.number().min(0.2).max(1.2), sunIntensity: z.number().min(0.3).max(5), ambient: z.number().min(0.1).max(1.5), clouds: z.number().min(0).max(2),
  }),
  land: z.object({
    grass: Hex, grassLight: Hex, dry: Hex, dirt: Hex, rock: Hex, sand: Hex, snow: Hex, water: Hex, foliage: Hex,
    fields: z.array(Hex).min(1).max(9),
    /** Altitude (m above the lowest point) where the snow starts. */
    snowLine: z.number().min(20).max(600),
    /** Colour of the distant mountains, whose foot then takes `grass` and top `snow`. Absent: grey rock under white snow. */
    peaks: Hex.optional(),
  }),
  decor: z.object({
    trees: z.number().min(0).max(2), conifers: frac, poplars: frac,
    rocks: z.number().min(0).max(2), grass: z.number().min(0).max(2), flowers: z.number().min(0).max(2), crowd: z.number().min(0).max(2),
  }),
  weather: z.enum(WEATHERS).default('clair'),
});
export type ThemeContent = z.infer<typeof ThemeContent>;

// ---------- Cosmetic (parametric jersey) ----------
/** Colour: hexadecimal, or relative to the colour chosen by the rider. */
export const Paint = z.union([z.string().regex(/^#[0-9a-f]{6}$/i), z.enum(['team', 'team-dark', 'team-light', 'white', 'black'])]);
/** SVG path reduced to geometry (d attribute): no tag, no script, no external link possible. */
export const SvgPathData = z.string().max(3000).regex(/^[MmLlHhVvCcSsQqTtAaZz0-9eE.,\s+-]+$/, 'Invalid SVG path');
export const JerseyLayer = z.discriminatedUnion('type', [
  z.object({ type: z.literal('gradient'), color: Paint, angle: z.number().min(0).max(360) }),
  z.object({ type: z.literal('stripes'), color: Paint, dir: z.enum(['h', 'v', 'd']), count: z.number().int().min(1).max(30), width: z.number().min(0.05).max(0.95), offset: frac.optional() }),
  z.object({ type: z.literal('band'), color: Paint, dir: z.enum(['h', 'v']), pos: frac, size: z.number().min(0.01).max(1) }),
  z.object({ type: z.literal('dots'), color: Paint, size: z.number().min(0.05).max(0.5), cell: z.number().int().min(6).max(64), stagger: z.boolean().optional() }),
  z.object({ type: z.literal('checker'), color: Paint, cell: z.number().int().min(6).max(64) }),
  z.object({ type: z.literal('chevrons'), color: Paint, count: z.number().int().min(1).max(12), width: z.number().min(0.05).max(0.5) }),
  z.object({ type: z.literal('text'), text: name(14), color: Paint, size: z.number().min(0.05).max(0.5), x: frac, y: frac, font: z.enum(['sans', 'serif', 'mono', 'display']).optional(), bold: z.boolean().optional() }),
  z.object({ type: z.literal('shape'), shape: z.enum(['star', 'bolt', 'heart', 'circle', 'diamond', 'triangle', 'flame', 'wheel']), color: Paint, x: frac, y: frac, size: z.number().min(0.03).max(1), rotate: z.number().min(-180).max(180).optional() }),
  z.object({ type: z.literal('path'), d: SvgPathData, color: Paint, x: frac, y: frac, size: z.number().min(0.03).max(1), view: z.number().min(1).max(2000) }),
  z.object({ type: z.literal('noise'), amount: z.number().min(0).max(0.3) }),
]);
export const JerseyDesign = z.object({ base: Paint, layers: z.array(JerseyLayer).max(16) });
export type JerseyDesign = z.infer<typeof JerseyDesign>;
export type JerseyLayer = z.infer<typeof JerseyLayer>;
export const FINISHES = ['brillant', 'mat', 'metal', 'chrome', 'nacre', 'carbone'] as const;
export const FrameDesign = z.object({ paint: Paint, finish: z.enum(FINISHES), accent: Paint.optional().describe('fork and stays') });
export const HelmetDesign = z.object({ paint: Paint, finish: z.enum(FINISHES), stripe: Paint.optional(), lens: z.enum(['fume', 'miroir', 'or', 'clair']).optional() });
export const WheelsDesign = z.object({ rim: Paint, depth: z.enum(['bas', 'moyen', 'haut', 'lenticulaire']), hub: Paint.optional(), sidewall: z.enum(['noir', 'beige', 'blanc']).default('noir') });
export type FrameDesign = z.infer<typeof FrameDesign>;
export type HelmetDesign = z.infer<typeof HelmetDesign>;
export type WheelsDesign = z.infer<typeof WheelsDesign>;
export const COSMETIC_SLOTS = ['jersey', 'frame', 'helmet', 'wheels'] as const;
export type CosmeticSlot = (typeof COSMETIC_SLOTS)[number];
export const CosmeticContent = z.discriminatedUnion('slot', [
  z.object({ slot: z.literal('jersey'), design: JerseyDesign }),
  z.object({ slot: z.literal('frame'), design: FrameDesign }),
  z.object({ slot: z.literal('helmet'), design: HelmetDesign }),
  z.object({ slot: z.literal('wheels'), design: WheelsDesign }),
]);
export type CosmeticContent = z.infer<typeof CosmeticContent>;

// ---------- Game ----------
export const GAME_ACTIONS = ['left', 'right', 'fire', 'up', 'down'] as const;
export const MECHANICS = ['moteur', 'batterie', 'zone', 'explosion', 'reserve', 'rythme', 'charge'] as const;
export const GameContent = z.object({
  /** JavaScript program that calls Gruvetto.game({...}); run in an isolated worker, without network or storage. */
  code: z.string().min(50).max(200_000),
  controls: z.array(z.object({ action: z.enum(GAME_ACTIONS), label: text(40) })).max(GAME_ACTIONS.length).default([]),
  mechanics: z.array(z.enum(MECHANICS)).max(MECHANICS.length).default([]),
  /** The game can dress up a workout (it reads the power target). */
  workout: z.boolean().default(false),
  /** Suggested length of a play (s). */
  duration: z.number().int().min(60).max(7200).optional(),
});
export type GameContent = z.infer<typeof GameContent>;

// ---------- Manifeste ----------
export const CONTENT_SCHEMAS = { workout: WorkoutContent, plan: PlanContent, track: TrackContent, mode: ModeContent, item: ItemContent, theme: ThemeContent, cosmetic: CosmeticContent, game: GameContent } as const;
export type ContentOf<K extends CreationKind> = z.infer<(typeof CONTENT_SCHEMAS)[K]>;

const base = { name: name(60), desc: text(400).default(''), tags: z.array(z.string().trim().toLowerCase().min(1).max(24)).max(8).default([]) };
/** Creation without publishing metadata: what the workshop produces and what the player edits. */
export const CreationInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('workout'), ...base, content: WorkoutContent }),
  z.object({ kind: z.literal('plan'), ...base, content: PlanContent }),
  z.object({ kind: z.literal('track'), ...base, content: TrackContent }),
  z.object({ kind: z.literal('mode'), ...base, content: ModeContent }),
  z.object({ kind: z.literal('item'), ...base, content: ItemContent }),
  z.object({ kind: z.literal('theme'), ...base, content: ThemeContent }),
  z.object({ kind: z.literal('cosmetic'), ...base, content: CosmeticContent }),
  z.object({ kind: z.literal('game'), ...base, content: GameContent }),
]);
export type CreationInput = z.infer<typeof CreationInput>;
export type CreationOf<K extends CreationKind> = Extract<CreationInput, { kind: K }>;

export const Provenance = z.object({ prompt: text(2000), model: text(60), at: z.number(), turns: z.number().int().min(1).max(50).optional() });
export type Provenance = z.infer<typeof Provenance>;
export const Visibility = z.enum(['public', 'unlisted', 'private']);
export const VersionRef = z.object({ id: CreationId, version: z.number().int().min(1) });

/** A published creation (detailed API response). */
export interface Creation<K extends CreationKind = CreationKind> {
  id: string; version: number; kind: K; name: string; desc: string; tags: string[]; content: ContentOf<K>;
  author: { id: string; name: string } | null; remixOf: { id: string; version: number } | null; visibility: z.infer<typeof Visibility>;
  price: number; provenance: Provenance | null; facts: Fact[]; stats: CreationStats; createdAt: number; updatedAt: number;
}
export type CreationSummary = Omit<Creation, 'content' | 'provenance'>;
export interface CreationStats { plays: number; finishes: number; rating: number | null; ratings: number; remixes: number }
/** Key figure shown on a card (duration, TSS, length, elevation…). */
export interface Fact { label: string; value: string }
export interface Issue { level: 'error' | 'warning'; message: string; path?: string }

// ---------- Routes ----------
export const PublishBody = z.object({
  creation: CreationInput,
  visibility: Visibility.default('public'),
  remixOf: VersionRef.optional(),
  /** Price in coins in the shop (cosmetics only). */
  price: z.number().int().min(0).max(1000).default(0),
  provenance: Provenance.optional(),
});
export type PublishBody = z.input<typeof PublishBody>;
export const PatchCreationBody = z.object({ visibility: Visibility.optional(), price: z.number().int().min(0).max(1000).optional() });
export const ListCreationsQuery = z.object({
  kind: CreationKind.optional(),
  q: z.string().trim().max(60).optional(),
  tag: z.string().trim().toLowerCase().max(24).optional(),
  sort: z.enum(['top', 'new', 'plays']).default('top'),
  mine: z.enum(['1', 'true']).optional(),
  /** Include the content of the current version of each creation (the store draws real thumbnails with it). */
  content: z.enum(['1', 'true']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(40),
  offset: z.coerce.number().int().min(0).max(10000).default(0),
});
/** A listed creation; `content` is present when the list was asked with `content=1`. */
export type ListedCreation = CreationSummary & { content?: unknown; hidden?: boolean; moderation?: 'ai' | 'local' };
export const RateBody = z.object({ stars: z.number().int().min(1).max(5) });
export const REPORT_REASONS = ['offensant', 'marque', 'donnees', 'triche', 'autre'] as const;
export const ReportBody = z.object({ reason: z.enum(REPORT_REASONS), note: text(300).optional() });
export const PlayBody = z.object({ finished: z.boolean() });
export const CreationParams = z.object({ id: CreationId });
export const CreationVersionParams = z.object({ id: CreationId, version: z.coerce.number().int().min(1) });

export const GenerateBody = z.object({
  kind: z.union([CreationKind, z.literal('auto')]).default('auto'),
  prompt: z.string().trim().min(3, 'Describe what you want in a few words').max(2000),
  /** Creation to tweak (content validated according to its type by the server). */
  base: z.object({ kind: CreationKind, name: text(60), desc: text(400).optional(), tags: z.array(z.string().max(24)).max(8).optional(), content: z.unknown() }).optional(),
  /** Previous workshop exchanges, for successive tweaks. */
  history: z.array(z.object({ role: z.enum(['user', 'assistant']), text: text(4000) })).max(12).default([]),
  /** Errors observed by the client while testing the creation (a game that crashes, for example). */
  feedback: text(4000).optional(),
});
export type GenerateBody = z.input<typeof GenerateBody>;
export interface GenerateResponse { kind: CreationKind; creation: CreationInput; notes: string; issues: Issue[]; facts: Fact[]; attempts: number; model: string }

// ---------- Collection (ADR 0017) ----------
/**
 * Id of something the store offers: an official entry (mode, track, workout, plan, theme, game, official cosmetic:
 * their catalogue ids, such as "race", "collines", "jersey-pois") or a published creation ("cr_…").
 */
export const StoreItemId = z.string().regex(/^[a-z0-9][a-z0-9_:-]{0,59}$/i, 'Invalid identifier');
export const AddToCollectionBody = z.object({ id: StoreItemId });
export const CollectionParams = z.object({ id: StoreItemId });
/**
 * An entry of the player's collection. Official entries carry only their id (the client holds their content);
 * a published creation comes with its current version, so it can be played offline and in a room.
 */
export interface CollectionItem { id: string; kind: CreationKind; addedAt: number; creation?: Creation }

// ---------- Rider outfit and body ----------
/** Worn cosmetic: embedded in the outfit to be rendered everywhere, including for other online players. */
export const Worn = z.object({ id: z.string().max(60), name: z.string().trim().max(60), content: CosmeticContent });
export type Worn = z.infer<typeof Worn>;
export const Wear = z.object({ jersey: Worn.optional(), frame: Worn.optional(), helmet: Worn.optional(), wheels: Worn.optional() })
  .refine(w => Object.entries(w).every(([slot, x]) => !x || x.content.slot === slot), 'Cosmetic worn in the wrong slot');
export type Wear = z.infer<typeof Wear>;
/** Customisable body and face (free): character, skin, calves, build, eyes, eyebrows, mouth, beard, glasses. */
export const Body = z.object({
  skin: Hex, hair: Hex, eyeColor: Hex,
  calves: z.number().min(0.7).max(1.8), build: z.number().min(0.85).max(1.2),
  eyes: z.enum(['ronds', 'amande', 'rieurs', 'endormis', 'etoiles']),
  brows: z.enum(['fins', 'epais', 'fronces', 'arques']),
  mouth: z.enum(['sourire', 'neutre', 'grimace', 'langue', 'dents']),
  beard: z.enum(['aucune', 'moustache', 'bouc', 'barbe', 'favoris']),
  glasses: z.enum(['aucune', 'sport', 'rondes', 'miroir']),
  freckles: z.boolean(),
  /** Bodies saved before there was a choice keep the character they were shown. */
  figure: z.enum(['male', 'female']).default('male'),
  /** Unset: the character's own cut (hairstyleOf). */
  hairstyle: z.enum(['short', 'shaved', 'ponytail', 'braid', 'long']).optional(),
});
export type Body = z.infer<typeof Body>;
export type Hairstyle = NonNullable<Body['hairstyle']>;
/** The cut a rider wears: chosen, or a ponytail for a woman and short hair for a man who never chose. */
export const hairstyleOf = (b: Pick<Body, 'hairstyle' | 'figure'>): Hairstyle => b.hairstyle ?? (b.figure === 'female' ? 'ponytail' : 'short');
export const DEFAULT_BODY: Body = { skin: '#e0ac86', hair: '#4a3222', eyeColor: '#4a6b8a', calves: 1, build: 1, eyes: 'ronds', brows: 'fins', mouth: 'sourire', beard: 'aucune', glasses: 'sport', freckles: false, figure: 'male' };
export interface WalletResponse { coins: number; owned: string[] }
